/**
 * sheetEngine — a dependency-free spreadsheet formula engine.
 *
 * Pure functions only (no React, no DOM) so it is trivially unit-testable and
 * can be imported without booting the app. Powers the Webtop "Sheets" app.
 *
 * Pipeline: raw cell strings → tokenize → Pratt-parse to an AST → evaluate with
 * lazy cell resolution + cycle detection. Supports cell refs (A1), 2D ranges
 * (A1:B3), arithmetic (+ - * / ^ %), string concat (&), comparisons
 * (= <> < > <= >=), unary +/-, ~50 Excel-compatible functions (incl. VLOOKUP,
 * INDEX/MATCH, SUMIF/COUNTIF, and dates), plus a number-format renderer.
 */

// ── A1 <-> {row,col} address helpers ────────────────────────────────────────
export const colLabel = (index) => {
  let n = index;
  let s = "";
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
};

export const colIndex = (label) => {
  let n = 0;
  const up = String(label).toUpperCase();
  for (let i = 0; i < up.length; i += 1) n = n * 26 + (up.charCodeAt(i) - 64);
  return n - 1;
};

export const parseRef = (ref) => {
  const m = /^\$?([A-Za-z]+)\$?([0-9]+)$/.exec(String(ref).trim());
  if (!m) return null;
  return { col: colIndex(m[1]), row: parseInt(m[2], 10) - 1 };
};

export const cellName = (row, col) => `${colLabel(col)}${row + 1}`;

// ── error values ─────────────────────────────────────────────────────────────
export const ERR = {
  DIV0: { error: "#DIV/0!" },
  VALUE: { error: "#VALUE!" },
  REF: { error: "#REF!" },
  NAME: { error: "#NAME?" },
  NUM: { error: "#NUM!" },
  CYCLE: { error: "#CYCLE!" },
  NA: { error: "#N/A" },
};
export const isError = (v) => v != null && typeof v === "object" && typeof v.error === "string";
const isRange = (v) => v != null && typeof v === "object" && v.__range === true;

// ── date serials (Excel epoch: 1899-12-30 = 0) ──────────────────────────────
const DATE_EPOCH = Date.UTC(1899, 11, 30);
const MS_DAY = 86400000;
export const dateToSerial = (y, m, d) => Math.round((Date.UTC(y, m - 1, d) - DATE_EPOCH) / MS_DAY);
export const serialToDate = (serial) => new Date(DATE_EPOCH + Math.round(serial) * MS_DAY);

// ── tokenizer ────────────────────────────────────────────────────────────────
const TOKEN_RE = [
  ["WS", /^\s+/],
  ["NUMBER", /^\d+(\.\d+)?([eE][+-]?\d+)?/],
  ["STRING", /^"([^"]*)"/],
  ["REF", /^\$?[A-Za-z]+\$?\d+/],
  ["FUNC", /^[A-Za-z_][A-Za-z0-9_.]*(?=\s*\()/],
  ["NAME", /^[A-Za-z_][A-Za-z0-9_.]*/],
  ["OP", /^(<=|>=|<>|[+\-*/^&%=<>])/],
  ["LPAREN", /^\(/],
  ["RPAREN", /^\)/],
  ["COMMA", /^,/],
  ["COLON", /^:/],
];

const tokenize = (src) => {
  const tokens = [];
  let s = src;
  while (s.length) {
    let matched = false;
    for (const [type, re] of TOKEN_RE) {
      const m = re.exec(s);
      if (m) {
        if (type !== "WS") tokens.push({ type, value: type === "STRING" ? m[1] : m[0] });
        s = s.slice(m[0].length);
        matched = true;
        break;
      }
    }
    if (!matched) throw ERR.VALUE;
  }
  return tokens;
};

// ── Pratt parser → AST ───────────────────────────────────────────────────────
const BP = { "=": 1, "<>": 1, "<": 1, ">": 1, "<=": 1, ">=": 1, "&": 2, "+": 3, "-": 3, "*": 4, "/": 4, "^": 6 };

const parse = (tokens) => {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  const parsePrimary = () => {
    const t = next();
    if (!t) throw ERR.VALUE;
    if (t.type === "NUMBER") return { kind: "num", value: parseFloat(t.value) };
    if (t.type === "STRING") return { kind: "str", value: t.value };
    if (t.type === "OP" && (t.value === "-" || t.value === "+")) {
      return { kind: "unary", op: t.value, arg: parseUnaryThenPostfix() };
    }
    if (t.type === "LPAREN") {
      const e = parseExpr(0);
      if (!peek() || peek().type !== "RPAREN") throw ERR.VALUE;
      next();
      return e;
    }
    if (t.type === "REF") {
      if (peek() && peek().type === "COLON") {
        next();
        const end = next();
        if (!end || end.type !== "REF") throw ERR.REF;
        return { kind: "range", start: t.value, end: end.value };
      }
      return { kind: "ref", value: t.value };
    }
    if (t.type === "FUNC") {
      next();
      const args = [];
      if (peek() && peek().type !== "RPAREN") {
        args.push(parseExpr(0));
        while (peek() && peek().type === "COMMA") {
          next();
          args.push(parseExpr(0));
        }
      }
      if (!peek() || peek().type !== "RPAREN") throw ERR.VALUE;
      next();
      return { kind: "call", name: t.value.toUpperCase(), args };
    }
    if (t.type === "NAME") {
      const up = t.value.toUpperCase();
      if (up === "TRUE") return { kind: "bool", value: true };
      if (up === "FALSE") return { kind: "bool", value: false };
      throw ERR.NAME;
    }
    throw ERR.VALUE;
  };

  const parseUnaryThenPostfix = () => {
    let node = parsePrimary();
    while (peek() && peek().type === "OP" && peek().value === "%") {
      next();
      node = { kind: "percent", arg: node };
    }
    return node;
  };

  const parseExpr = (minBp) => {
    let left = parseUnaryThenPostfix();
    while (peek() && peek().type === "OP" && BP[peek().value] != null && BP[peek().value] > minBp) {
      const op = next().value;
      const nextMin = op === "^" ? BP[op] - 1 : BP[op];
      const right = parseExpr(nextMin);
      left = { kind: "binary", op, left, right };
    }
    return left;
  };

  const ast = parseExpr(0);
  if (pos !== tokens.length) throw ERR.VALUE;
  return ast;
};

// ── value coercion ───────────────────────────────────────────────────────────
const toNumber = (v) => {
  if (isError(v)) throw v;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (v === "" || v == null) return 0;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return 0;
    const n = Number(t);
    if (!Number.isNaN(n)) return n;
    throw ERR.VALUE;
  }
  throw ERR.VALUE;
};
const toText = (v) => {
  if (isError(v)) throw v;
  if (v == null) return "";
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  return String(v);
};
const toBool = (v) => {
  if (isError(v)) throw v;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const u = v.trim().toUpperCase();
    if (u === "TRUE") return true;
    if (u === "FALSE" || u === "") return false;
  }
  return !!v;
};

const flatten = (args) => {
  const out = [];
  for (const a of args) {
    if (isRange(a)) out.push(...a.list);
    else out.push(a);
  }
  return out;
};
const numbersOf = (args) =>
  flatten(args)
    .filter((v) => typeof v === "number" || typeof v === "boolean")
    .map((v) => (typeof v === "boolean" ? (v ? 1 : 0) : v));

// Match a value against an Excel-style criterion: 5, ">5", "<=3", "<>x", "te*t".
const matchCriteria = (value, criterion) => {
  if (isRange(criterion)) return false;
  let crit = criterion;
  if (typeof crit === "number" || typeof crit === "boolean") {
    return value === crit || toText(value) === toText(crit);
  }
  crit = String(crit ?? "");
  const m = /^(<=|>=|<>|=|<|>)(.*)$/.exec(crit);
  if (m) {
    const op = m[1];
    const rhsRaw = m[2].trim();
    const rhsNum = Number(rhsRaw);
    const bothNum = typeof value === "number" && rhsRaw !== "" && !Number.isNaN(rhsNum);
    const a = bothNum ? value : toText(value).toLowerCase();
    const b = bothNum ? rhsNum : rhsRaw.toLowerCase();
    switch (op) {
      case "=": return a === b;
      case "<>": return a !== b;
      case "<": return a < b;
      case ">": return a > b;
      case "<=": return a <= b;
      case ">=": return a >= b;
      default: return false;
    }
  }
  // Plain text criterion — supports * and ? wildcards, case-insensitive.
  if (/[*?]/.test(crit)) {
    const re = new RegExp(`^${crit.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*").replace(/\?/g, ".")}$`, "i");
    return re.test(toText(value));
  }
  return toText(value).toLowerCase() === crit.toLowerCase();
};

const gridOf = (arg) => (isRange(arg) ? arg.grid : [[arg]]);
const listOf = (arg) => (isRange(arg) ? arg.list : [arg]);

// ── function library ─────────────────────────────────────────────────────────
const FN = {
  SUM: (a) => numbersOf(a).reduce((s, n) => s + n, 0),
  PRODUCT: (a) => numbersOf(a).reduce((s, n) => s * n, 1),
  AVERAGE: (a) => {
    const ns = numbersOf(a);
    if (!ns.length) throw ERR.DIV0;
    return ns.reduce((s, n) => s + n, 0) / ns.length;
  },
  MEDIAN: (a) => {
    const ns = numbersOf(a).sort((x, y) => x - y);
    if (!ns.length) throw ERR.NUM;
    const mid = Math.floor(ns.length / 2);
    return ns.length % 2 ? ns[mid] : (ns[mid - 1] + ns[mid]) / 2;
  },
  COUNT: (a) => numbersOf(a).length,
  COUNTA: (a) => flatten(a).filter((v) => v !== "" && v != null).length,
  COUNTBLANK: (a) => flatten(a).filter((v) => v === "" || v == null).length,
  MIN: (a) => {
    const ns = numbersOf(a);
    return ns.length ? Math.min(...ns) : 0;
  },
  MAX: (a) => {
    const ns = numbersOf(a);
    return ns.length ? Math.max(...ns) : 0;
  },
  ABS: (a) => Math.abs(toNumber(a[0])),
  SIGN: (a) => Math.sign(toNumber(a[0])),
  SQRT: (a) => {
    const n = toNumber(a[0]);
    if (n < 0) throw ERR.NUM;
    return Math.sqrt(n);
  },
  POWER: (a) => Math.pow(toNumber(a[0]), toNumber(a[1])),
  EXP: (a) => Math.exp(toNumber(a[0])),
  LN: (a) => Math.log(toNumber(a[0])),
  LOG: (a) => Math.log(toNumber(a[0])) / Math.log(toNumber(a[1] ?? 10)),
  LOG10: (a) => Math.log10(toNumber(a[0])),
  MOD: (a) => {
    const d = toNumber(a[1]);
    if (d === 0) throw ERR.DIV0;
    return toNumber(a[0]) - d * Math.floor(toNumber(a[0]) / d);
  },
  INT: (a) => Math.floor(toNumber(a[0])),
  TRUNC: (a) => {
    const f = 10 ** toNumber(a[1] ?? 0);
    return Math.trunc(toNumber(a[0]) * f) / f;
  },
  ROUND: (a) => {
    const f = 10 ** toNumber(a[1] ?? 0);
    return Math.round(toNumber(a[0]) * f) / f;
  },
  ROUNDUP: (a) => {
    const f = 10 ** toNumber(a[1] ?? 0);
    const n = toNumber(a[0]) * f;
    return (n < 0 ? Math.floor(n) : Math.ceil(n)) / f;
  },
  ROUNDDOWN: (a) => {
    const f = 10 ** toNumber(a[1] ?? 0);
    const n = toNumber(a[0]) * f;
    return (n < 0 ? Math.ceil(n) : Math.floor(n)) / f;
  },
  CEILING: (a) => {
    const sig = toNumber(a[1] ?? 1) || 1;
    return Math.ceil(toNumber(a[0]) / sig) * sig;
  },
  FLOOR: (a) => {
    const sig = toNumber(a[1] ?? 1) || 1;
    return Math.floor(toNumber(a[0]) / sig) * sig;
  },
  PI: () => Math.PI,
  // logical
  IF: (a) => (toBool(a[0]) ? a[1] ?? true : a[2] ?? false),
  IFS: (a) => {
    for (let i = 0; i + 1 < a.length; i += 2) if (toBool(a[i])) return a[i + 1];
    throw ERR.NA;
  },
  AND: (a) => flatten(a).every((v) => toBool(v)),
  OR: (a) => flatten(a).some((v) => toBool(v)),
  XOR: (a) => flatten(a).filter((v) => toBool(v)).length % 2 === 1,
  NOT: (a) => !toBool(a[0]),
  IFERROR: (a) => (isError(a[0]) ? a[1] ?? "" : a[0]),
  IFNA: (a) => (isError(a[0]) && a[0].error === "#N/A" ? a[1] ?? "" : a[0]),
  ISBLANK: (a) => a[0] === "" || a[0] == null,
  ISNUMBER: (a) => typeof a[0] === "number",
  ISTEXT: (a) => typeof a[0] === "string" && a[0] !== "",
  ISERROR: (a) => isError(a[0]),
  // conditional aggregates
  SUMIF: (a) => {
    const vals = listOf(a[0]);
    const sums = a[2] != null ? listOf(a[2]) : vals;
    let total = 0;
    vals.forEach((v, i) => {
      if (matchCriteria(v, a[1]) && typeof sums[i] === "number") total += sums[i];
    });
    return total;
  },
  COUNTIF: (a) => listOf(a[0]).filter((v) => matchCriteria(v, a[1])).length,
  AVERAGEIF: (a) => {
    const vals = listOf(a[0]);
    const avg = a[2] != null ? listOf(a[2]) : vals;
    let total = 0;
    let n = 0;
    vals.forEach((v, i) => {
      if (matchCriteria(v, a[1]) && typeof avg[i] === "number") {
        total += avg[i];
        n += 1;
      }
    });
    if (!n) throw ERR.DIV0;
    return total / n;
  },
  // lookup
  VLOOKUP: (a) => {
    const key = a[0];
    const grid = gridOf(a[1]);
    const col = toNumber(a[2]) - 1;
    const exact = a[3] == null ? false : !toBool(a[3]) ? true : false;
    for (let r = 0; r < grid.length; r += 1) {
      const cell = grid[r][0];
      const hit = typeof key === "number" && typeof cell === "number" ? cell === key : toText(cell).toLowerCase() === toText(key).toLowerCase();
      if (hit) return grid[r][col] != null ? grid[r][col] : "";
    }
    if (!exact) {
      // approximate: largest value <= key (assumes sorted ascending)
      let best = null;
      for (let r = 0; r < grid.length; r += 1) {
        const cell = grid[r][0];
        if (typeof cell === "number" && typeof key === "number" && cell <= key) best = grid[r][col];
      }
      if (best != null) return best;
    }
    throw ERR.NA;
  },
  HLOOKUP: (a) => {
    const key = a[0];
    const grid = gridOf(a[1]);
    const row = toNumber(a[2]) - 1;
    const header = grid[0] || [];
    for (let c = 0; c < header.length; c += 1) {
      const cell = header[c];
      const hit = typeof key === "number" && typeof cell === "number" ? cell === key : toText(cell).toLowerCase() === toText(key).toLowerCase();
      if (hit) return grid[row] && grid[row][c] != null ? grid[row][c] : "";
    }
    throw ERR.NA;
  },
  INDEX: (a) => {
    const grid = gridOf(a[0]);
    const r = toNumber(a[1]) - 1;
    const c = a[2] != null ? toNumber(a[2]) - 1 : 0;
    if (r < 0 || r >= grid.length) throw ERR.REF;
    const rowArr = grid[r];
    if (c < 0 || c >= rowArr.length) throw ERR.REF;
    return rowArr[c] != null ? rowArr[c] : "";
  },
  MATCH: (a) => {
    const key = a[0];
    const list = listOf(a[1]);
    const type = a[2] != null ? toNumber(a[2]) : 1;
    if (type === 0) {
      for (let i = 0; i < list.length; i += 1) {
        const v = list[i];
        const hit = typeof key === "number" && typeof v === "number" ? v === key : toText(v).toLowerCase() === toText(key).toLowerCase();
        if (hit) return i + 1;
      }
      throw ERR.NA;
    }
    // approximate: last value <= key (type 1, ascending)
    let idx = -1;
    for (let i = 0; i < list.length; i += 1) {
      if (typeof list[i] === "number" && typeof key === "number" && list[i] <= key) idx = i;
    }
    if (idx < 0) throw ERR.NA;
    return idx + 1;
  },
  // text
  CONCAT: (a) => flatten(a).map((v) => toText(v)).join(""),
  CONCATENATE: (a) => a.map((v) => (isRange(v) ? v.list.map(toText).join("") : toText(v))).join(""),
  TEXTJOIN: (a) => {
    const delim = toText(a[0]);
    const skip = toBool(a[1]);
    const parts = flatten(a.slice(2)).map(toText).filter((s) => (skip ? s !== "" : true));
    return parts.join(delim);
  },
  LEN: (a) => toText(a[0]).length,
  LEFT: (a) => toText(a[0]).slice(0, toNumber(a[1] ?? 1)),
  RIGHT: (a) => {
    const n = toNumber(a[1] ?? 1);
    return n <= 0 ? "" : toText(a[0]).slice(-n);
  },
  MID: (a) => {
    const start = toNumber(a[1]) - 1;
    return toText(a[0]).substr(Math.max(0, start), toNumber(a[2]));
  },
  FIND: (a) => {
    const i = toText(a[1]).indexOf(toText(a[0]), toNumber(a[2] ?? 1) - 1);
    if (i < 0) throw ERR.VALUE;
    return i + 1;
  },
  SEARCH: (a) => {
    const i = toText(a[1]).toLowerCase().indexOf(toText(a[0]).toLowerCase(), toNumber(a[2] ?? 1) - 1);
    if (i < 0) throw ERR.VALUE;
    return i + 1;
  },
  SUBSTITUTE: (a) => toText(a[0]).split(toText(a[1])).join(toText(a[2])),
  REPLACE: (a) => {
    const s = toText(a[0]);
    const start = toNumber(a[1]) - 1;
    const len = toNumber(a[2]);
    return s.slice(0, start) + toText(a[3]) + s.slice(start + len);
  },
  REPT: (a) => toText(a[0]).repeat(Math.max(0, toNumber(a[1]))),
  UPPER: (a) => toText(a[0]).toUpperCase(),
  LOWER: (a) => toText(a[0]).toLowerCase(),
  PROPER: (a) => toText(a[0]).replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()),
  TRIM: (a) => toText(a[0]).trim().replace(/\s+/g, " "),
  VALUE: (a) => toNumber(a[0]),
  TEXT: (a) => toText(a[0]),
  TRUE: () => true,
  FALSE: () => false,
  // dates (Excel serials)
  TODAY: () => {
    const n = new Date();
    return dateToSerial(n.getFullYear(), n.getMonth() + 1, n.getDate());
  },
  NOW: () => (Date.now() - DATE_EPOCH) / MS_DAY,
  DATE: (a) => dateToSerial(toNumber(a[0]), toNumber(a[1]), toNumber(a[2])),
  YEAR: (a) => serialToDate(toNumber(a[0])).getUTCFullYear(),
  MONTH: (a) => serialToDate(toNumber(a[0])).getUTCMonth() + 1,
  DAY: (a) => serialToDate(toNumber(a[0])).getUTCDate(),
  WEEKDAY: (a) => serialToDate(toNumber(a[0])).getUTCDay() + 1,
  DATEDIF: (a) => Math.abs(toNumber(a[1]) - toNumber(a[0])),
};

// ── evaluator ────────────────────────────────────────────────────────────────
const compare = (op, l, r) => {
  let a = l;
  let b = r;
  if (typeof a === "boolean") a = a ? 1 : 0;
  if (typeof b === "boolean") b = b ? 1 : 0;
  if (typeof a !== "number" || typeof b !== "number") {
    a = toText(l).toLowerCase();
    b = toText(r).toLowerCase();
  }
  switch (op) {
    case "=": return a === b;
    case "<>": return a !== b;
    case "<": return a < b;
    case ">": return a > b;
    case "<=": return a <= b;
    case ">=": return a >= b;
    default: throw ERR.VALUE;
  }
};

const evalNode = (node, resolve) => {
  switch (node.kind) {
    case "num": return node.value;
    case "str": return node.value;
    case "bool": return node.value;
    case "ref": {
      const p = parseRef(node.value);
      if (!p) return ERR.REF;
      return resolve(cellName(p.row, p.col));
    }
    case "range": {
      const s = parseRef(node.start);
      const e = parseRef(node.end);
      if (!s || !e) return ERR.REF;
      const r0 = Math.min(s.row, e.row);
      const r1 = Math.max(s.row, e.row);
      const c0 = Math.min(s.col, e.col);
      const c1 = Math.max(s.col, e.col);
      const gridArr = [];
      const list = [];
      for (let r = r0; r <= r1; r += 1) {
        const row = [];
        for (let c = c0; c <= c1; c += 1) {
          const v = resolve(cellName(r, c));
          row.push(v);
          list.push(v);
        }
        gridArr.push(row);
      }
      return { __range: true, rows: r1 - r0 + 1, cols: c1 - c0 + 1, grid: gridArr, list };
    }
    case "unary": {
      const v = evalNode(node.arg, resolve);
      try {
        return node.op === "-" ? -toNumber(v) : +toNumber(v);
      } catch (e) {
        return e;
      }
    }
    case "percent": {
      const v = evalNode(node.arg, resolve);
      try {
        return toNumber(v) / 100;
      } catch (e) {
        return e;
      }
    }
    case "binary": {
      const l = evalNode(node.left, resolve);
      const r = evalNode(node.right, resolve);
      if (isError(l)) return l;
      if (isError(r)) return r;
      if (isRange(l) || isRange(r)) return ERR.VALUE;
      try {
        switch (node.op) {
          case "+": return toNumber(l) + toNumber(r);
          case "-": return toNumber(l) - toNumber(r);
          case "*": return toNumber(l) * toNumber(r);
          case "/": {
            const d = toNumber(r);
            if (d === 0) return ERR.DIV0;
            return toNumber(l) / d;
          }
          case "^": return Math.pow(toNumber(l), toNumber(r));
          case "&": return toText(l) + toText(r);
          default: return compare(node.op, l, r);
        }
      } catch (e) {
        return isError(e) ? e : ERR.VALUE;
      }
    }
    case "call": {
      const fn = FN[node.name];
      if (!fn) return ERR.NAME;
      const args = node.args.map((a) => evalNode(a, resolve));
      // Functions that legitimately inspect errors get them raw; others short-circuit.
      const ERROR_TOLERANT = { IFERROR: 1, IFNA: 1, ISERROR: 1, ISBLANK: 1, ISNUMBER: 1, ISTEXT: 1 };
      if (!ERROR_TOLERANT[node.name]) {
        const bad = args.find((v) => isError(v));
        if (bad) return bad;
      }
      try {
        return fn(args);
      } catch (e) {
        return isError(e) ? e : ERR.VALUE;
      }
    }
    default:
      return ERR.VALUE;
  }
};

const parseLiteral = (raw) => {
  if (typeof raw !== "string") return raw;
  const t = raw.trim();
  if (t === "") return "";
  const u = t.toUpperCase();
  if (u === "TRUE") return true;
  if (u === "FALSE") return false;
  if (/^[+-]?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/.test(t)) {
    const n = Number(t);
    if (!Number.isNaN(n)) return n;
  }
  return raw;
};

export const evaluateCells = (cells) => {
  const cache = {};
  const visiting = new Set();
  const resolve = (addr) => {
    if (Object.prototype.hasOwnProperty.call(cache, addr)) return cache[addr];
    if (visiting.has(addr)) {
      cache[addr] = ERR.CYCLE;
      return cache[addr];
    }
    const raw = cells[addr];
    if (raw == null || raw === "") {
      cache[addr] = "";
      return "";
    }
    if (typeof raw === "string" && raw[0] === "=") {
      visiting.add(addr);
      let val;
      try {
        val = evalNode(parse(tokenize(raw.slice(1))), resolve);
      } catch (e) {
        val = isError(e) ? e : ERR.VALUE;
      }
      visiting.delete(addr);
      if (isRange(val)) val = ERR.VALUE;
      cache[addr] = val;
      return val;
    }
    cache[addr] = parseLiteral(raw);
    return cache[addr];
  };
  const out = {};
  for (const addr of Object.keys(cells)) out[addr] = resolve(addr);
  return out;
};

// ── display formatting ───────────────────────────────────────────────────────
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad2 = (n) => String(n).padStart(2, "0");
const groupThousands = (intStr) => intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Render a numeric value per a number format spec: { t, d } where
// t ∈ gen|num|comma|cur|pct|date|datetime, d = decimals.
export const formatNumber = (value, fmt) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return formatValue(value);
  const t = (fmt && fmt.t) || "gen";
  const d = fmt && fmt.d != null ? fmt.d : t === "cur" ? 2 : 0;
  if (t === "date" || t === "datetime") {
    const dt = serialToDate(value);
    const base = `${MONTHS[dt.getUTCMonth()]} ${pad2(dt.getUTCDate())}, ${dt.getUTCFullYear()}`;
    if (t === "datetime") {
      const frac = value - Math.floor(value);
      const mins = Math.round(frac * 24 * 60);
      return `${base} ${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`;
    }
    return base;
  }
  if (t === "pct") return `${(value * 100).toFixed(d)}%`;
  const dd = t === "cur" || t === "acc" ? (fmt && fmt.d != null ? fmt.d : 2) : d;
  const neg = value < 0;
  const abs = Math.abs(value);
  let s = abs.toFixed(t === "num" || t === "comma" || t === "cur" || t === "acc" ? dd : 0);
  if (dd === 0 && t === "gen") s = String(abs);
  if (t === "comma" || t === "cur" || t === "acc") {
    const [int, frac] = s.split(".");
    s = groupThousands(int) + (frac ? `.${frac}` : "");
  }
  if (t === "cur") return `${neg ? "-" : ""}$${s}`;
  if (t === "acc") return neg ? `($${s})` : `$${s}`; // accounting: negatives in parens
  return neg ? `-${s}` : s;
};

export const formatValue = (v) => {
  if (v == null || v === "") return "";
  if (isError(v)) return v.error;
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return "#NUM!";
    return String(Math.round(v * 1e10) / 1e10);
  }
  return String(v);
};

// Grid display: apply a cell's number format to numeric values, else plain.
export const displayCell = (value, fmt) => {
  if (typeof value === "number" && fmt && fmt.t && fmt.t !== "gen") return formatNumber(value, fmt);
  return formatValue(value);
};

export const evaluateOne = (raw, cells = {}) => {
  const probe = { __PROBE__: raw, ...cells };
  return evaluateCells(probe).__PROBE__;
};

/**
 * translateFormula — shift the relative cell references in a formula by (dRow,
 * dCol). `$`-anchored parts stay fixed (e.g. $A$1 unchanged, A$1 shifts col only).
 * Refs inside "quoted strings" and function names (LOG10, ATAN2) are left alone.
 * Used by copy/paste (relative) and the fill handle. Returns the raw string for
 * non-formulas. Out-of-bounds refs become #REF!.
 */
export const translateFormula = (raw, dRow, dCol) => {
  if (typeof raw !== "string" || raw[0] !== "=" || (dRow === 0 && dCol === 0)) return raw;
  const body = raw.slice(1);
  // Match a quoted string OR a complete cell ref. The trailing lookahead forbids
  // a following letter/digit/( so the ref can't partial-match inside a function
  // name with digits (LOG10) — \d+ must consume the whole number.
  const re = /"[^"]*"|(?<![A-Za-z0-9_.])(\$?)([A-Za-z]+)(\$?)(\d+)(?![A-Za-z0-9(.])/g;
  const shifted = body.replace(re, (m, colD, col, rowD, row) => {
    if (m[0] === '"') return m; // string literal — leave it
    let c = colIndex(col);
    let r = parseInt(row, 10) - 1;
    if (!colD) c += dCol;
    if (!rowD) r += dRow;
    if (c < 0 || r < 0) return "#REF!";
    return `${colD}${colLabel(c)}${rowD}${r + 1}`;
  });
  return `=${shifted}`;
};
